/**
 * Shared factory for fact-find asset rows (manual Add and statement import).
 */
import { ASSET_TYPES } from '../../constants/index.ts';
import type { AssetType, FactFindAsset } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { StatementImportDraft } from './statementImportStub.tsx';
import type { ImportedLiabilityDraft } from '../../../../shared/src/calculation/statementImport.ts';
import { entitiesIn, entityNameKey } from '../../../../shared/src/calculation/statementImport.ts';
import { entityOwnershipValue } from '../../../../shared/src/calculation/constants/clientStructureTypes.ts';
import { createEmptyStructure } from '../../constants/clientStructureTypes.ts';
import { newEntityId } from '../../utils/entityId.ts';
import { INVESTMENT_PROFILES, ASSET_CLASS_ASSUMPTIONS, investmentBondNetPctFor } from '../../../../shared/src/calculation/investmentProfiles.ts';

export type DraftAsset = FactFindAsset;

interface AssetDefaultRates {
    incomeRate: number;
    growthRate: number;
    frankingCredit?: number;
}

/** Computed at CALL time, never module load, so a licensee's own capital market assumptions
 *  (applied to the shared table at sign-in) drive the presets a new asset starts on. */
export function getAssetDefaultRates(): Record<AssetType, AssetDefaultRates> {
    const ac = ASSET_CLASS_ASSUMPTIONS;
    return {
        bankAccounts: { incomeRate: ac.bankIncomePct, growthRate: 0 },
        termDeposits: { incomeRate: ac.termDepositIncomePct, growthRate: 0 },
        shares: { incomeRate: ac.sharesIncomePct, growthRate: ac.sharesGrowthPct, frankingCredit: ac.sharesFrankingPct },
        // A bond's growth is the Balanced portfolio net of the life office's 30% tax; nothing is paid
        // out (its earnings stay inside), so incomeRate is 0 by design.
        investmentBonds: { incomeRate: 0, growthRate: investmentBondNetPctFor('balanced') },
        investmentProperty: { incomeRate: ac.propertyYieldPct, growthRate: ac.propertyGrowthPct },
        // Super wrappers start on the published Balanced presets (investmentProfiles.ts) — the most
        // common fund default — visible and editable on the card, never a silent stamp. Their
        // incomeRate is 0 by design: nothing personal flows from inside the wrapper (an ABP's cash
        // is its pension payment, a separate field), and the old 4% here was dead weight.
        superannuation: { incomeRate: 0, growthRate: INVESTMENT_PROFILES.balanced.accumulationNetPct },
        accountBasedPensions: { incomeRate: 0, growthRate: INVESTMENT_PROFILES.balanced.pensionNetPct },
        annuities: { incomeRate: 4, growthRate: 0 },
        funeralBonds: { incomeRate: 0, growthRate: 0 },
        // Neither grows: a prepaid funeral is a contract, and a loan is worth what is still owed.
        prepaidFuneral: { incomeRate: 0, growthRate: 0 },
        loansToOthers: { incomeRate: 0, growthRate: 0 },
        personalAssets: { incomeRate: 0, growthRate: 0 },
        other: { incomeRate: 0, growthRate: 0 },
    };
}

/**
 * @param type - ASSET_TYPES id
 * @param overrides - description, value, ownership, id, etc.
 */
export function buildNewAsset(type: AssetType, isPartnered: boolean, overrides: Partial<DraftAsset> = {}): DraftAsset {
    const assetConfig = ASSET_TYPES.find((t) => t.id === type);
    if (!assetConfig) {
        throw new Error(`Unknown asset type: ${type}`);
    }
    const isIndividual = assetConfig.individualOnly;
    const defaultOwnership = isPartnered && !isIndividual ? 'joint' : 'client';

    const newAsset: DraftAsset = {
        id: newEntityId(),
        description: '',
        ownership: defaultOwnership,
        hasCgt: assetConfig.hasCgt || false,
        ...getAssetDefaultRates()[type],
        acquisitionDate: 'post',
        reinvest: true,
        frankingCredit: 70,
        commencementDateOption: 'post-2015',
        commencementDate: '',
        commencementDateUnsure: false,
        fundingSource: 'super',
        maturityDate: '',
        ...overrides,
        type,
    };

    return newAsset;
}

export function appendImportedAssetDrafts(
    factFind: DraftFactFind,
    drafts: StatementImportDraft[],
    isPartnered: boolean,
): DraftFactFind {
    const assets = { ...factFind.assets };

    drafts.forEach((draft) => {
        if (!draft?.assetTypeId) return;
        const { assetTypeId, description, value, ownership } = draft;
        const parsedValue = value != null && value !== '' ? parseFloat(value) : NaN;
        // buildNewAsset mints its own unique id; no need to offset a shared timestamp any more. The
        // document's fields (cost base, commencement date, payment…) land on the card as entered values,
        // over the type's defaults, so the adviser edits rather than re-keys.
        const newAsset = buildNewAsset(assetTypeId as AssetType, isPartnered, {
            ...(draft.fields || {}),
            ...(description != null ? { description } : {}),
            ...(!Number.isNaN(parsedValue) ? { value: parsedValue } : {}),
            ...(ownership ? { ownership } : {}),
        });
        const list = assets[assetTypeId] || [];
        assets[assetTypeId] = [...list, newAsset];
    });

    return { ...factFind, assets };
}

/**
 * The whole import on the profile (15 Oct 2026): the entities the ticked rows name are matched to the
 * profile's structures by name or created (the Trusts & companies gate opens with them); their assets
 * and liabilities are owned as `entity:<id>`; personal rows land as they are. Pure — the caller sets state.
 */
export function applyStatementImport(
    factFind: DraftFactFind,
    picked: { assets: StatementImportDraft[]; liabilities: ImportedLiabilityDraft[] },
    isPartnered: boolean,
): DraftFactFind {
    let next: DraftFactFind = { ...factFind, structures: [...(factFind.structures || [])] };
    const idByEntity = new Map<string, string>();
    for (const entity of entitiesIn(picked.assets, picked.liabilities)) {
        const key = entityNameKey(entity.name);
        const existing = (next.structures || []).find((s) => entityNameKey(s.name) === key);
        if (existing?.id) { idByEntity.set(key, existing.id); continue; }
        // A new arrangement starts at the ordinary split — the whole of it counted toward the household,
        // half each for a couple — so its assets are not shown at nothing until the adviser visits it.
        const created = {
            ...createEmptyStructure(),
            name: entity.name,
            type: entity.type,
            apportionmentClient: isPartnered ? 50 : 100,
            apportionmentPartner: isPartnered ? 50 : 0,
        };
        next = { ...next, structuresGateAnswer: true, structures: [...(next.structures || []), created] };
        idByEntity.set(key, created.id as string);
    }
    const owned = <T extends { entity: { name: string } | null; ownership: string }>(d: T): T =>
        d.entity ? { ...d, ownership: entityOwnershipValue(idByEntity.get(entityNameKey(d.entity.name)) || '') } : d;

    next = appendImportedAssetDrafts(next, picked.assets.filter((d) => d.selected && d.assetTypeId).map(owned), isPartnered);

    // The family home's value fills Living Arrangements — only where nothing is recorded there yet.
    const homeRow = picked.assets.find((d) => d.selected && d.kind === 'home' && Number(d.value) > 0);
    if (homeRow && !(Number((next.home as { value?: unknown } | undefined)?.value) > 0)) {
        next = { ...next, home: { ...(next.home || {}), value: Number(homeRow.value) } } as DraftFactFind;
    }

    const liabilities = { ...(next.liabilities || {}) } as Record<string, Array<Record<string, unknown>>>;
    picked.liabilities.filter((d) => d.selected && d.liabilityTypeId).map(owned).forEach((d) => {
        const type = d.liabilityTypeId as string;
        const balance = parseFloat(d.balance);
        const row = {
            id: newEntityId(),
            type,
            description: d.description,
            frequency: 'monthly',
            assetLink: 'none',
            ownership: d.ownership,
            loanType: 'pi',
            repaymentCalculation: type === 'mortgages' || type === 'personalLoans' ? 'auto' : 'manual',
            ...(Number.isFinite(balance) ? { balance } : {}),
            ...d.fields,
        };
        liabilities[type] = [...(liabilities[type] || []), row];
    });
    return { ...next, liabilities } as DraftFactFind;
}
